/**
 * DynamicRequestForm Component
 *
 * Renders form controls based on field definitions from requestSpec.
 * Used in form-first mode for request threads.
 *
 * Field type → UI control mapping:
 * - boolean → Yes/No toggle buttons
 * - enum → Radio group
 * - enum_multi → Checkbox list
 * - string → TextField
 * - string_long → TextField multiline
 * - number → TextField type="number"
 * - date → TextField type="date"
 * - file → Upload control (placeholder for now)
 */

import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  FormHelperText,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import { isFieldEmpty } from '../../utils/request-thread-ui';

export interface AllowedField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  description?: string;
  enumValues?: string[];
}

export interface DynamicRequestFormProps {
  allowedFields: AllowedField[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

export default function DynamicRequestForm({
  allowedFields,
  values,
  onChange,
  errors = {},
  disabled = false,
}: DynamicRequestFormProps) {
  if (!allowedFields || allowedFields.length === 0) {
    return (
      <Alert severity="info">
        No specific fields defined. Please use the chat below to provide information.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {allowedFields.map((field) => (
        <FieldRenderer
          key={field.key}
          field={field}
          value={values[field.key]}
          onChange={(value) => onChange(field.key, value)}
          error={errors[field.key]}
          disabled={disabled}
        />
      ))}
    </Box>
  );
}

interface FieldRendererProps {
  field: AllowedField;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
}

/** Stable wrapper so inputs are not remounted on re-render (preserves focus). */
function FieldWrapper({
  children,
  error,
  hasError,
}: {
  children: React.ReactNode;
  error?: string;
  hasError?: boolean;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: hasError ? 'error.main' : 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {children}
      {error && (
        <FormHelperText error sx={{ mt: 1 }}>
          {error}
        </FormHelperText>
      )}
    </Paper>
  );
}

function FieldRenderer({ field, value, onChange, error, disabled }: FieldRendererProps) {
  const hasError = Boolean(error);
  const isEmpty = isFieldEmpty(value);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const showRequired = field.required && isEmpty;

  switch (field.type) {
    case 'boolean':
      return (
        <FieldWrapper error={error} hasError={hasError}>
          <BooleanField
            field={field}
            value={value as boolean | null | undefined}
            onChange={onChange}
            disabled={disabled}
          />
        </FieldWrapper>
      );

    case 'enum':
      return (
        <FieldWrapper error={error} hasError={hasError}>
          <EnumField
            field={field}
            value={value as string | null | undefined}
            onChange={onChange}
            disabled={disabled}
          />
        </FieldWrapper>
      );

    case 'enum_multi':
      return (
        <FieldWrapper error={error} hasError={hasError}>
          <EnumMultiField
            field={field}
            value={value as string[] | null | undefined}
            onChange={onChange}
            disabled={disabled}
          />
        </FieldWrapper>
      );

    case 'string_long':
      return (
        <FieldWrapper error={error} hasError={hasError}>
          <TextAreaField
            field={field}
            value={value as string | null | undefined}
            onChange={onChange}
            disabled={disabled}
            hasError={hasError}
          />
        </FieldWrapper>
      );

    case 'number':
      return (
        <FieldWrapper error={error} hasError={hasError}>
          <NumberField
            field={field}
            value={value as number | null | undefined}
            onChange={onChange}
            disabled={disabled}
            hasError={hasError}
          />
        </FieldWrapper>
      );

    case 'date':
      return (
        <FieldWrapper error={error} hasError={hasError}>
          <DateField
            field={field}
            value={value as string | null | undefined}
            onChange={onChange}
            disabled={disabled}
            hasError={hasError}
          />
        </FieldWrapper>
      );

    case 'file':
      return (
        <FieldWrapper error={error} hasError={hasError}>
          <FileField field={field} value={value} onChange={onChange} disabled={disabled} />
        </FieldWrapper>
      );

    case 'string':
    default:
      return (
        <FieldWrapper error={error} hasError={hasError}>
          <StringField
            field={field}
            value={value as string | null | undefined}
            onChange={onChange}
            disabled={disabled}
            hasError={hasError}
          />
        </FieldWrapper>
      );
  }
}

// ============================================================================
// Individual Field Components
// ============================================================================

interface BooleanFieldProps {
  field: AllowedField;
  value: boolean | null | undefined;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

function BooleanField({ field, value, onChange, disabled }: BooleanFieldProps) {
  return (
    <FormControl fullWidth>
      <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
        {field.label}
        {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
      </FormLabel>
      {field.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {field.description}
        </Typography>
      )}
      <ToggleButtonGroup
        exclusive
        value={value === true ? 'yes' : value === false ? 'no' : null}
        onChange={(_, newValue) => {
          if (newValue === 'yes') onChange(true);
          else if (newValue === 'no') onChange(false);
        }}
        disabled={disabled}
        sx={{ gap: 1 }}
      >
        <ToggleButton
          value="yes"
          sx={{
            px: 4,
            '&.Mui-selected': { bgcolor: 'success.light', color: 'success.contrastText' },
          }}
        >
          Yes
        </ToggleButton>
        <ToggleButton
          value="no"
          sx={{
            px: 4,
            '&.Mui-selected': { bgcolor: 'error.light', color: 'error.contrastText' },
          }}
        >
          No
        </ToggleButton>
      </ToggleButtonGroup>
    </FormControl>
  );
}

interface EnumFieldProps {
  field: AllowedField;
  value: string | null | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
}

function EnumField({ field, value, onChange, disabled }: EnumFieldProps) {
  const options = field.enumValues || [];
  const useDropdown = options.length >= 5;

  return (
    <FormControl fullWidth>
      <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
        {field.label}
        {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
      </FormLabel>
      {field.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {field.description}
        </Typography>
      )}
      {useDropdown ? (
        <Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          displayEmpty
          aria-label={field.label}
          sx={{ mt: 0.5 }}
        >
          <MenuItem value="">
            <em>Select...</em>
          </MenuItem>
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <RadioGroup value={value || ''} onChange={(e) => onChange(e.target.value)}>
          {options.map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              control={<Radio disabled={disabled} />}
              label={option}
              sx={{
                '& .MuiFormControlLabel-label': { fontSize: '0.95rem' },
              }}
            />
          ))}
        </RadioGroup>
      )}
    </FormControl>
  );
}

interface EnumMultiFieldProps {
  field: AllowedField;
  value: string[] | null | undefined;
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

function EnumMultiField({ field, value, onChange, disabled }: EnumMultiFieldProps) {
  const options = field.enumValues || [];
  const selectedValues = value || [];

  const handleToggle = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter((v) => v !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  return (
    <FormControl fullWidth>
      <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
        {field.label}
        {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
      </FormLabel>
      {field.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {field.description}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Select all that apply
      </Typography>
      <FormGroup>
        {options.map((option) => (
          <FormControlLabel
            key={option}
            control={
              <Checkbox
                checked={selectedValues.includes(option)}
                onChange={() => handleToggle(option)}
                disabled={disabled}
              />
            }
            label={option}
            sx={{
              '& .MuiFormControlLabel-label': { fontSize: '0.95rem' },
            }}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
}

interface StringFieldProps {
  field: AllowedField;
  value: string | null | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
}

function StringField({ field, value, onChange, disabled, hasError }: StringFieldProps) {
  return (
    <FormControl fullWidth>
      <TextField
        label={field.label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
        disabled={disabled}
        error={hasError}
        helperText={field.description}
        fullWidth
        variant="outlined"
      />
    </FormControl>
  );
}

function TextAreaField({ field, value, onChange, disabled, hasError }: StringFieldProps) {
  return (
    <FormControl fullWidth>
      <TextField
        label={field.label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
        disabled={disabled}
        error={hasError}
        helperText={field.description}
        fullWidth
        multiline
        rows={4}
        variant="outlined"
      />
    </FormControl>
  );
}

interface NumberFieldProps {
  field: AllowedField;
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  hasError?: boolean;
}

function NumberField({ field, value, onChange, disabled, hasError }: NumberFieldProps) {
  return (
    <FormControl fullWidth>
      <TextField
        label={field.label}
        type="number"
        value={value ?? ''}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '') {
            onChange(null);
          } else {
            onChange(parseFloat(val));
          }
        }}
        required={field.required}
        disabled={disabled}
        error={hasError}
        helperText={field.description}
        fullWidth
        variant="outlined"
      />
    </FormControl>
  );
}

interface DateFieldProps {
  field: AllowedField;
  value: string | null | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
}

function DateField({ field, value, onChange, disabled, hasError }: DateFieldProps) {
  // Parse ISO date to input date format (YYYY-MM-DD)
  let inputValue = '';
  if (value) {
    try {
      const date = new Date(value);
      inputValue = date.toISOString().split('T')[0];
    } catch {
      inputValue = value;
    }
  }

  return (
    <FormControl fullWidth>
      <TextField
        label={field.label}
        type="date"
        value={inputValue}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
        disabled={disabled}
        error={hasError}
        helperText={field.description}
        fullWidth
        variant="outlined"
        InputLabelProps={{ shrink: true }}
      />
    </FormControl>
  );
}

interface FileFieldProps {
  field: AllowedField;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

function FileField({ field, disabled }: FileFieldProps) {
  // File upload is a placeholder - will integrate with existing upload path
  return (
    <FormControl fullWidth>
      <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
        {field.label}
        {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
      </FormLabel>
      {field.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {field.description}
        </Typography>
      )}
      <Box
        sx={{
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          bgcolor: 'grey.50',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Typography color="text.secondary">
          File upload will be available via chat below
        </Typography>
      </Box>
    </FormControl>
  );
}
